import React, { Suspense } from 'react';
import { DashboardComponentProps } from './types';

// =====================================================================
// DashboardHost — resolves a dashboard NAME to its component.
// ---------------------------------------------------------------------
// Resolution is by CONVENTION, not a registry file:
//
//     backend descriptor name  ->  src/dashboards/components/<name>.tsx
//
// Deliberate: the director's kit drops component files in and never edits
// a shared registry, so a kit import cannot break the lookup for every
// other dashboard. The name/file agreement is checked by the importer,
// which fails the upload when a descriptor has no component (or vice
// versa). That check lives in the BACKEND repo
// (scripts/check-dashboard-halves.js); the plan doc it's described in is
// provider-portal-backend/docs/business-reports-plan.md, not this repo.
//
// React.lazy + a dynamic import means webpack code-splits each dashboard
// into its own chunk, so the portal bundle doesn't grow with every
// dashboard the director adds.
// =====================================================================

// Webpack reports a stale/missing chunk as ChunkLoadError (or a "Loading chunk
// N failed" message); a component that genuinely isn't in the build fails to
// resolve instead. Different cause, different fix.
const isChunkLoadError = (err: any): boolean =>
    err?.name === 'ChunkLoadError' || /Loading chunk|failed to fetch dynamically imported/i.test(err?.message || '');

// Cache the lazy components so switching dashboards back and forth doesn't
// re-create the component type (which would remount and refetch).
const cache: { [name: string]: React.LazyExoticComponent<React.FC<DashboardComponentProps>> } = {};

const loadDashboard = (name: string) => {
    if (!cache[name]) {
        // @vite-ignore: webpack (the portal's bundler) resolves this templated
        // path by globbing the directory, which is exactly what we want. Vite —
        // used by the kit's standalone harness — cannot analyze a path with no
        // file extension and warns on every load. The harness never takes this
        // branch (it injects a resolver built from import.meta.glob), so the
        // comment just silences noise the director would otherwise see.
        cache[name] = React.lazy(() =>
            import(/* @vite-ignore */ `./components/${name}`).catch((err) => ({
                // A broken component must not take down the admin panel. The two
                // causes need DIFFERENT advice, so don't collapse them: a stale
                // browser holding an old chunk hash after a deploy is fixed by
                // reloading, while a genuinely absent component needs a
                // developer. Treating both as "missing from this build" sends
                // people to the wrong fix in the case they can solve themselves.
                default: () => (
                    <div className={'alert alert-warning'}>
                        {isChunkLoadError(err) ? (
                            <>
                                This dashboard couldn't finish loading. The portal was
                                probably updated while this page was open —{' '}
                                <strong>reload the page</strong> and it should come back.
                            </>
                        ) : (
                            <>
                                This dashboard's display component could not be loaded
                                {` ("${name}")`}. Its definition exists on the server but the
                                component is missing from this build — the kit may have
                                imported only half. Contact a developer.
                            </>
                        )}
                    </div>
                ),
            })),
        );
    }
    return cache[name];
};

interface DashboardHostProps extends DashboardComponentProps {
    name: string;
}

export const DashboardHost: React.FC<DashboardHostProps> = ({ name, ...props }) => {
    const Component = loadDashboard(name);
    return (
        <Suspense fallback={<div className={'p-4 text-muted'}>Loading dashboard…</div>}>
            <Component {...props} />
        </Suspense>
    );
};
